import {AfterViewInit, Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import { ApplicationLogDetailsRequest, ApplicationLogStepRequest, Step } from "leia-api";
import { AddressVerificationResponse } from 'skyhopper-api';
import {OnboardingRequest} from "src/app/shared/model/onboarding-request.model";
import {State} from "src/app/shared/model/state";
import {FacadeService} from "src/app/shared/service-facade/facade.service";
import {UtilitiesService} from "src/app/shared/services/utilities.service";
import {OnboardingApplicant} from "../../shared/model/onboarding-applicant.model";
import StateEnum = Step.StateEnum;
import NameEnum = Step.NameEnum;
import {SuggestedAddressService} from "../../shared/services/suggested-address.service";
import {SuggestedAddressRules} from "./rules/suggested-address-rules.abstract";
import {SuggestedAddressRulesFactory} from "./rules/suggested-address-rules.factory";
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import { AddressVerificationService } from 'src/app/core/services/address-verification.service';
export interface SuggestedAddressResponse {
  didAcceptSuggestion: boolean;
  address: SuggestedAddress;
}

export interface SuggestedAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateId: number;
  zip: string;
}

@Component({
  selector: 'app-suggested-address',
  templateUrl: './suggested-address.component.html',
  styleUrls: ['./suggested-address.component.css'],
  providers: [
    {
      provide: SuggestedAddressRules,
      useFactory: SuggestedAddressRulesFactory,
      deps: [
        FacadeService
      ]
    }
  ]
})
export class SuggestedAddressComponent implements OnInit, AfterViewInit {
  applicantId: number;
  uspsAddress: AddressVerificationResponse;

  shoppingCartOnboardingRequest: OnboardingRequest;

  inputtedAddress1 = '';
  inputtedAddress2 = '';
  inputtedCity = '';
  inputtedState = '';
  inputtedZip = '';

  suggestedAddress1 = '';
  suggestedAddress2 = '';
  suggestedCity = '';
  suggestedState = '';
  suggestedZip = '';

  stateList: State[] = [];
  
  addressVerificationHeadingId: string = '';

  constructor(
    private dialogRef: MatDialogRef<SuggestedAddressComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SuggestedAddressDialogData,
    private facade: FacadeService,
    private utils: UtilitiesService,
    private suggestedAddressService: SuggestedAddressService,
    public suggestedAddressRules: SuggestedAddressRules,
    private addressVerificationService: AddressVerificationService) {
      this.shoppingCartOnboardingRequest = new OnboardingRequest();
      this.applicantId = data.applicantId;
      this.uspsAddress = data.uspsAddress;
  }

  ngOnInit(): void {
    this.shoppingCartOnboardingRequest = this.facade.getShoppingCartOnboardingRequest();
    this.stateList = this.facade.getAllStates();
    this.getUSPSAddress();
  }

  ngAfterViewInit(): void {
    const ele: any = document.querySelector('mat-dialog-container');
    const wrapperEle: any = document.querySelector('mat-dialog-container div');
    //settimeout used because aria-labelledby is getting render after sometimes
    setTimeout(() => {
      const dynamicElementTitle: any = ele.getAttribute('aria-labelledby');
      if(wrapperEle) {
        wrapperEle.removeAttribute('id');
      }
      if(ele && dynamicElementTitle) {
        this.addressVerificationHeadingId = dynamicElementTitle;
      }
    }, 0);
  }

  keepInputted(): void {
    this.dialogRef.close();
  }

  async useSuggested(): Promise<void> {
    const applicationRequest = this.facade.getShoppingCartApplicationLogsRequest();

    const log = this.utils.createLog("BB8", "Changing address to USPS suggested address.")
    const step = this.utils.createStep(NameEnum.TellUsAboutYourself, StateEnum.Success, [log]);

    let applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.TellUsAboutYourself, OnboardingStateEnum.Success, OnboardingSourceEnum.Bb8,[this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8,"Changing address to USPS suggested address.")]);

    // add to address object
    let suggestedAddressResponse: SuggestedAddressResponse = {
      didAcceptSuggestion: true,
      address: {
        addressLine1: this.suggestedAddress1,
        addressLine2: this.suggestedAddress2,
        city: this.suggestedCity,
        stateId: this.stateList.find(state => state.stateAbbr === this.suggestedState)!.id,
        zip: this.suggestedZip
      }
    }

    applicationRequest.applicants?.forEach(value => {
      // If there are joint applicants and they have the same address - update them both.
      if(value.applicantId === this.applicantId || this.shoppingCartOnboardingRequest.copyAddressFromPrimary) {
        const address = value.addresses![0];
        address.streetAddress = this.suggestedAddress1;
        address.streetAddress1 = this.suggestedAddress2;
        address.city = this.suggestedCity;
        address.state = this.stateList.find(state => state.stateAbbr === this.suggestedState)!.stateAbbr;
        address.zip = this.suggestedZip;
      }
    });

    applicationRequest.step = step;
    applicationRequest.applicationLogStepRequest = applicationLogStepRequest;

    this.facade.setShoppingCartApplicationLogsRequest(applicationRequest);
    await this.facade.updateApplication();

    const onboardingRequest = this.facade.getShoppingCartOnboardingRequest();

    onboardingRequest.applicants?.forEach(value => {
      // If there are joint applicants and they have the same address - update them both.
      if(value.applicantId === this.applicantId || this.shoppingCartOnboardingRequest.copyAddressFromPrimary) {
        value.tellUsAboutYourself.addressLine1 = this.suggestedAddress1;
        value.tellUsAboutYourself.addressLine2 = this.suggestedAddress2;
        value.tellUsAboutYourself.city = this.suggestedCity;
        value.tellUsAboutYourself.state = this.stateList.find(state => state.stateAbbr === this.suggestedState)!.stateName;
        value.tellUsAboutYourself.zipcode = this.suggestedZip;
      }
    });

    this.facade.setShoppingCartOnboardingRequest(onboardingRequest);

    this.dialogRef.close(suggestedAddressResponse);
  }

  private getUSPSAddress(): void {
    let onboardingRequest = this.facade.getShoppingCartOnboardingRequest();
    let onboardingApplicant : OnboardingApplicant = new OnboardingApplicant();

    onboardingRequest.applicants?.forEach(value => {
      if(value.applicantId === this.applicantId) { onboardingApplicant = value; }
    });

    const {
      addressLine1: inputtedAddress1,
      addressLine2: inputtedAddress2,
      city: inputtedCity,
      state: inputtedState,
      zipcode: inputtedZip  } = onboardingApplicant.tellUsAboutYourself;

    this.inputtedAddress1 = inputtedAddress1;
    this.inputtedAddress2 = inputtedAddress2;
    this.inputtedCity = inputtedCity;
    this.inputtedState = inputtedState;
    this.inputtedZip = inputtedZip;

    const {
      streetAddress, streetAddress1, city, state, zip
    } = this.uspsAddress.address!;

    this.suggestedAddress1 = streetAddress!;
    this.suggestedAddress2 = streetAddress1!;
    this.suggestedCity = city!;
    this.suggestedState = state!;
    this.suggestedZip = zip!;
  }

  closeButton() {
    this.suggestedAddressService.isCloseButtonActivated = true;
    this.dialogRef.close();
    this.addressVerificationService.popupAction.next(false);
  }
}

export interface SuggestedAddressDialogData {
  applicantId: number;
  uspsAddress: AddressVerificationResponse;
}
